import type { ComponentType, ReactElement } from 'react';
import { FontIcon, getColorFromString, getIcon, isDark } from '@fluentui/react';
import { tokens } from '@fluentui/react-components';
import * as Mdl2Icons from '@fluentui/react-icons-mdl2';
import * as FluentIcons from '@fluentui/react-icons';

export const statusColors = (
  color?: string
): { background: string; foreground: string } => {
  const parsed = color ? getColorFromString(color) : undefined;
  if (!parsed) {
    return {
      background: tokens.colorBrandBackground2,
      foreground: tokens.colorBrandForeground1,
    };
  }
  return {
    background: parsed.str,
    foreground: isDark(parsed)
      ? tokens.colorNeutralForegroundInverted
      : tokens.colorNeutralForeground1,
  };
};

const asComponent = (value: unknown): ComponentType | undefined => {
  if (typeof value === 'function') return value as ComponentType;
  if (value && typeof value === 'object' && 'default' in value) {
    const inner = (value as { default: unknown }).default;
    if (typeof inner === 'function') return inner as ComponentType;
  }
};

const lookup = (mod: object, icon: string): ComponentType | undefined => {
  const record = mod as Record<string, unknown>;
  const names = [
    `${icon}Icon`,
    icon,
    `${icon}24Regular`,
    `${icon}Regular`,
    `${icon}24Filled`,
  ];
  for (const name of names) {
    const Comp = asComponent(record[name]);
    if (Comp) return Comp;
  }
  const want = icon.replace(/Icon$/, '').toLowerCase();
  for (const [key, value] of Object.entries(record)) {
    const base = key
      .replace(/Icon$/, '')
      .replace(/24Regular$/, '')
      .replace(/Regular$/, '')
      .replace(/24Filled$/, '')
      .toLowerCase();
    if (base === want) {
      const Comp = asComponent(value);
      if (Comp) return Comp;
    }
  }
};

const iconStyle = {
  width: '1em',
  height: '1em',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  lineHeight: 0,
  overflow: 'hidden',
  fill: 'currentColor',
} as const;

export const statusIcon = (
  icon?: string,
  mdl2Only = false
): ReactElement | undefined => {
  if (!icon) return undefined;
  const Comp =
    lookup(Mdl2Icons, icon) ??
    (mdl2Only ? undefined : lookup(FluentIcons, icon));
  if (Comp) {
    return (
      <span style={iconStyle}>
        <Comp />
      </span>
    );
  }
  if (getIcon(icon)) {
    return <FontIcon iconName={icon} style={iconStyle} />;
  }
  return undefined;
};
